import { LANGUAGES, type Language, LanguageSchema } from '@shefira/shared';

import {
  getMusicVolume,
  getSfxVolume,
  isMuted,
  onMutedChange,
  onVolumeChange,
  setMuted,
  setMusicVolume,
  setSfxVolume,
} from '../audio/sound.js';
import {
  bindingLabel,
  bindingOf,
  captureBinding,
  isAction,
  KEY_ACTION_IDS,
  KEY_ACTIONS,
  type KeyAction,
  onBindingsChange,
  resetBindings,
  swapBinding,
} from '../settings/keybindings.js';
import {
  onPreferenceChange,
  type PreferenceKey,
  preference,
  setPreference,
} from '../settings/preferences.js';
import { ENGLISH_OPEN, language, setLanguage } from '../i18n/language.js';
import { clearAccount } from '../net/account.js';
import { LANGUAGE_NAMES, languageFlag } from './language-toggle.js';
import { clearSession, loadSession } from '../net/session.js';
import { confirmGuestSignOut, isGuestSession } from './guest-reminder.js';
import { requestHome } from './home.js';
import { closeButton, hudBar, hudShortcutButton } from './hud.js';
import { uiFormat, uiText } from './text.js';
import './profile.css';
import './settings.css';
import { kitTitle } from './kit.js';
import { isTouch } from './touch.js';
import { isNativeApp } from '../platform/native.js';

/**
 * The settings window (ADR 0029): the gear at the right of the top row, or
 * the settings key. Three tabs — sound (the master switch and one volume
 * each for the effects and the music), keys (every shortcut rebound by
 * pressing the new key) and display (damage numbers, combat frame, minimap,
 * ping, fullscreen). Under them, whatever the tab, the way out of the game:
 * back to the home screen, or signed out.
 *
 * One for the whole visit, like the sound switch: a zone change or a
 * reconnect leaves it where it is.
 */
type Tab = 'sound' | 'keys' | 'display';
const TABS: readonly [Tab, string][] = [
  ['sound', 'Son'],
  ['keys', 'Touches'],
  ['display', 'Affichage'],
];
/** How long a refused key stays marked. */
const NOTICE_MS = 1800;

const DISPLAY_SWITCHES: readonly [PreferenceKey, string][] = [
  ['damageNumbers', 'Chiffres de dégâts'],
  ['combatFrame', 'Cadre rouge en combat'],
  ['lowHealthAlert', 'Alerte de vie basse'],
  ['minimap', 'Mini-carte'],
  ['chat', 'Afficher le chat'],
  ['screenShake', "Secousses et éclairs de l'écran"],
  ['nightAndWeather', 'Nuit et météo'],
  ['vibration', 'Vibration aux coups reçus'],
  ['ping', 'Afficher le ping'],
];

/**
 * What this device can use (ADR 0043): a phone has no keys to rebind, and
 * only a phone that can buzz is offered the vibration.
 */
function offered(key: PreferenceKey): boolean {
  if (key !== 'vibration') return true;
  return isTouch() && typeof navigator.vibrate === 'function';
}

function paintSwitch(button: HTMLButtonElement, on: boolean): void {
  button.textContent = uiText(on ? 'Oui' : 'Non');
  button.setAttribute('aria-pressed', String(on));
}

class SettingsPanel {
  private readonly root: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly tabs = new Map<Tab, HTMLButtonElement>();
  private readonly bodies = new Map<Tab, HTMLElement>();
  private readonly keyButtons = new Map<KeyAction, HTMLButtonElement>();
  private readonly keyRows = new Map<KeyAction, HTMLElement>();
  private readonly keyHint: HTMLElement;
  private listening: KeyAction | undefined;
  private noticeTimer: number | undefined;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (this.listening !== undefined || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.key === 'Escape') {
      if (!this.root.hidden) this.toggle(false);
      return;
    }
    if (!isAction(event, 'settings')) return;
    event.preventDefault();
    this.toggle();
  };
  /** While a key is asked for: the next press is the binding, nothing else hears it. */
  private readonly onCapture = (event: KeyboardEvent): void => {
    const action = this.listening;
    if (action === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    if (/^(Shift|Control|Alt|Meta)/.test(event.code)) return;
    this.stopListening();
    if (event.key === 'Escape') return;
    const binding = captureBinding(event);
    if (binding === undefined) {
      this.notice(action, 'Touche réservée');
      return;
    }
    const moved = swapBinding(action, binding);
    if (moved !== undefined) {
      this.notice(
        moved,
        uiFormat('Échangée avec {action}', { action: uiText(KEY_ACTIONS[action]) }),
        action,
      );
    }
  };

  constructor() {
    this.root = document.createElement('section');
    this.root.className = 'profile settings';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', uiText('Réglages'));

    const frame = document.createElement('div');
    frame.className = 'profile__window settings__window';
    const title = document.createElement('h2');
    title.className = 'settings__title';
    title.textContent = uiText('Réglages');
    kitTitle(title, 'settings');
    const tabBar = document.createElement('div');
    tabBar.className = 'login__tabs profile__tabs';
    for (const [id, label] of TABS) {
      if (id === 'keys' && isTouch()) continue;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'login__tab';
      tab.textContent = uiText(label);
      tab.addEventListener('click', () => {
        this.showTab(id);
      });
      this.tabs.set(id, tab);
      tabBar.append(tab);
    }
    this.keyHint = document.createElement('p');
    this.keyHint.className = 'settings__hint';
    frame.append(
      title,
      tabBar,
      this.buildSound(),
      this.buildKeys(),
      this.buildDisplay(),
      this.buildExits(),
      closeButton('Fermer (Échap)', () => {
        this.toggle(false);
      }),
    );
    this.root.append(frame);
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.toggle(false);
    });
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown);
    onBindingsChange(() => {
      this.paintKeys();
    });
    this.showTab('sound');

    this.button = hudShortcutButton('settings', 'Réglages', 'settings', 'top');
    this.button.addEventListener('click', () => {
      this.toggle();
    });
  }

  /** At the right end of the top row, after the profile, whatever was rebuilt. */
  mountButton(): void {
    hudBar('top').append(this.button);
  }

  toggle(open = this.root.hidden): void {
    if (!open) this.stopListening();
    this.root.hidden = !open;
  }

  private showTab(id: Tab): void {
    this.stopListening();
    for (const [tabId, tab] of this.tabs) {
      tab.classList.toggle('login__tab--active', tabId === id);
    }
    for (const [bodyId, body] of this.bodies) body.hidden = bodyId !== id;
  }

  private body(id: Tab): HTMLElement {
    const body = document.createElement('div');
    body.className = 'settings__body';
    this.bodies.set(id, body);
    return body;
  }

  private row(label: string, control: HTMLElement): HTMLElement {
    const row = document.createElement('div');
    row.className = 'settings__row';
    const name = document.createElement('span');
    name.className = 'settings__label';
    name.textContent = uiText(label);
    row.append(name, control);
    return row;
  }

  private makeButton(label: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hud-button';
    button.textContent = uiText(label);
    return button;
  }

  /** A yes / no button, its state in `aria-pressed`. */
  private makeSwitch(on: boolean, onChange: (on: boolean) => void): HTMLButtonElement {
    const button = this.makeButton('');
    button.classList.add('settings__switch');
    paintSwitch(button, on);
    button.addEventListener('click', () => {
      const next = button.getAttribute('aria-pressed') !== 'true';
      paintSwitch(button, next);
      onChange(next);
    });
    return button;
  }

  private volume(
    label: string,
    read: () => number,
    apply: (value: number) => void,
  ): { row: HTMLElement; slider: HTMLInputElement } {
    const wrap = document.createElement('div');
    wrap.className = 'settings__volume';
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.step = '5';
    slider.className = 'settings__slider';
    slider.setAttribute('aria-label', uiText(label));
    const value = document.createElement('span');
    value.className = 'settings__value';
    const paint = (): void => {
      value.textContent = uiFormat('{value} %', { value: slider.value });
    };
    const follow = (): void => {
      slider.value = String(Math.round(read() * 100));
      paint();
    };
    follow();
    onVolumeChange(follow);
    slider.addEventListener('input', () => {
      paint();
      apply(Number(slider.value) / 100);
    });
    wrap.append(slider, value);
    return { row: this.row(label, wrap), slider };
  }

  private buildSound(): HTMLElement {
    const body = this.body('sound');
    const master = this.makeSwitch(!isMuted(), (on) => {
      setMuted(!on);
    });
    const sfx = this.volume('Effets', getSfxVolume, setSfxVolume);
    const music = this.volume('Musique', getMusicVolume, setMusicVolume);
    const follow = (muted: boolean): void => {
      paintSwitch(master, !muted);
      sfx.slider.disabled = muted;
      music.slider.disabled = muted;
    };
    onMutedChange(follow);
    follow(isMuted());
    body.append(this.row('Son', master), sfx.row, music.row);
    return body;
  }

  private buildKeys(): HTMLElement {
    const body = this.body('keys');
    this.keyHint.textContent = uiText(
      'Clique une touche puis appuie sur la nouvelle. Échap annule ; Entrée et Échap sont réservées.',
    );
    body.append(this.keyHint);
    for (const action of KEY_ACTION_IDS) {
      // Kept in the settings schema for older saved accounts; the hall itself
      // is now the sole entry point, so its former global shortcut is hidden.
      if (action === 'auction') continue;
      // No shop in the apps (ADR 0075), so no key for it either.
      if (action === 'shop' && isNativeApp()) continue;
      const button = this.makeButton('');
      button.classList.add('settings__key');
      button.addEventListener('click', () => {
        if (this.listening === action) this.stopListening();
        else this.listen(action);
      });
      this.keyButtons.set(action, button);
      const row = this.row(KEY_ACTIONS[action], button);
      this.keyRows.set(action, row);
      body.append(row);
    }
    const actions = document.createElement('div');
    actions.className = 'settings__actions';
    const reset = this.makeButton('Touches par défaut');
    reset.addEventListener('click', () => {
      this.stopListening();
      resetBindings();
    });
    actions.append(reset);
    body.append(actions);
    this.paintKeys();
    return body;
  }

  /** The language list, its current flag beside it. */
  private languageSelect(choices: readonly Language[]): HTMLElement {
    const picker = document.createElement('span');
    picker.className = 'settings__language';
    const select = document.createElement('select');
    select.className = 'settings__select';
    select.setAttribute('aria-label', uiText('Langue'));
    for (const which of choices) {
      const option = new Option(LANGUAGE_NAMES[which], which, false, which === language());
      option.lang = which;
      select.append(option);
    }
    select.addEventListener('change', () => {
      const chosen = LanguageSchema.safeParse(select.value);
      if (!chosen.success) return;
      select.disabled = true;
      setLanguage(chosen.data);
    });
    const wrap = document.createElement('span');
    wrap.className = 'settings__select-wrap';
    wrap.append(select);
    picker.append(languageFlag(language()), wrap);
    return picker;
  }

  private buildDisplay(): HTMLElement {
    const body = this.body('display');
    // The language (ADR 0069): a list, so one more language is one more line
    // of `LANGUAGES`; each named in itself, never translated; a change reloads.
    const offeredLanguages = LANGUAGES.filter(
      (which) => which === 'fr' || ENGLISH_OPEN || which === language(),
    );
    if (offeredLanguages.length > 1) body.append(this.row('Langue', this.languageSelect(offeredLanguages)));
    const switches = new Map<PreferenceKey, HTMLButtonElement>();
    for (const [key, label] of DISPLAY_SWITCHES) {
      if (!offered(key)) continue;
      const toggle = this.makeSwitch(preference(key), (on) => {
        setPreference(key, on);
      });
      switches.set(key, toggle);
      body.append(this.row(label, toggle));
    }
    // The account's saved settings may land after the window is built.
    onPreferenceChange((key) => {
      const toggle = switches.get(key);
      if (toggle !== undefined) paintSwitch(toggle, preference(key));
    });
    // The apps already fill the screen (ADR 0075).
    if (document.fullscreenEnabled && !isNativeApp()) {
      const fullscreen = this.makeButton('');
      const paint = (): void => {
        fullscreen.textContent = uiText(
          document.fullscreenElement === null ? 'Entrer' : 'Quitter',
          'fullscreen',
        );
      };
      paint();
      document.addEventListener('fullscreenchange', paint);
      fullscreen.addEventListener('click', () => {
        const request =
          document.fullscreenElement === null
            ? document.documentElement.requestFullscreen()
            : document.exitFullscreen();
        request.catch(() => {
          /* the browser said no; the button stays as it was */
        });
      });
      body.append(this.row('Plein écran', fullscreen));
    }
    return body;
  }

  /**
   * Leaving the game is a fresh page either way (the Phaser instance holds
   * one room): home keeps the session and opens the roster, signing out
   * forgets it and opens the login.
   */
  private buildExits(): HTMLElement {
    const exits = document.createElement('div');
    exits.className = 'settings__actions settings__exits';
    const home = this.makeButton("Retour à l'accueil");
    home.addEventListener('click', () => {
      requestHome();
    });
    const logout = this.makeButton('Se déconnecter');
    logout.addEventListener('click', () => {
      void this.signOut();
    });
    exits.append(home, logout);
    return exits;
  }

  /** A guest is warned first: signing out loses the account for good. */
  private async signOut(): Promise<void> {
    const session = loadSession();
    if (session !== null && (await isGuestSession(session.token))) {
      this.toggle(false);
      if (!(await confirmGuestSignOut())) return;
    }
    clearAccount();
    clearSession();
    window.location.reload();
  }

  private paintKeys(): void {
    for (const [action, button] of this.keyButtons) {
      const listening = this.listening === action;
      button.textContent = uiText(listening ? 'Appuie…' : bindingLabel(bindingOf(action)), 'key');
      button.classList.toggle('settings__key--listening', listening);
    }
  }

  private listen(action: KeyAction): void {
    this.stopListening();
    this.listening = action;
    window.addEventListener('keydown', this.onCapture, { capture: true });
    this.paintKeys();
  }

  private stopListening(): void {
    if (this.listening === undefined) return;
    this.listening = undefined;
    window.removeEventListener('keydown', this.onCapture, { capture: true });
    this.paintKeys();
  }

  /** Why a key was refused, shown on its button for a moment. */
  private notice(action: KeyAction, text: string, also?: KeyAction): void {
    window.clearTimeout(this.noticeTimer);
    const button = this.keyButtons.get(action);
    if (button !== undefined) button.textContent = uiText(text);
    const marked = [action, also].filter((id): id is KeyAction => id !== undefined);
    for (const id of marked) this.keyRows.get(id)?.classList.add('settings__row--conflict');
    this.noticeTimer = window.setTimeout(() => {
      for (const id of marked) this.keyRows.get(id)?.classList.remove('settings__row--conflict');
      this.paintKeys();
    }, NOTICE_MS);
  }
}

let panel: SettingsPanel | undefined;

/** The gear in the top row, right of the profile, and its window; built once. */
export function showSettingsPanel(): void {
  panel ??= new SettingsPanel();
  panel.mountButton();
}
